import React, { useEffect, useRef, useState } from 'react';
import { wrap } from '@components/wee/wee-timeline';
import { createFrameCadence } from '@components/wee/frame-cadence';
import {
  CAUSTIC_PERIOD,
  CAUSTIC_SPEED,
  STILL_T,
  SWIRL_PERIOD,
  WAKE,
  WAKE_PERIOD,
  stageMetrics,
  storyFrame,
  type Wake,
} from './water-field';
import { createWaterGl, type WaterGl } from './water-gl';
import { createWaterStars } from './water-stars';
import { useWaterStory, type WaterStory } from './water-story';
import { Layer, NightVeil, StarsCanvas, WaterCanvas } from './water-background.styles';

/**
 * The calm water behind every page — the backdrop the app's Explore and
 * Enact pages stand on (home-ceo-mobile `WeeBackdrop story={false}`) —
 * which tells the app's home story instead while the WEE section asks it
 * to (`WaterStory`). Mounted once, beneath the router, so it runs on
 * through navigation instead of restarting per page. Content paints over
 * it; glass surfaces blur it.
 */
export const WaterBackground: React.FC = () => {
  const layerRef = useRef<HTMLDivElement>(null);
  const waterRef = useRef<HTMLCanvasElement>(null);
  const starsRef = useRef<HTMLCanvasElement>(null);
  const veilRef = useRef<HTMLDivElement>(null);
  const story = useWaterStory();
  const [lit, setLit] = useState(false);

  useEffect(() => {
    const layer = layerRef.current;
    const water = waterRef.current;
    const stars = starsRef.current;
    const veil = veilRef.current;
    if (!layer || !water || !stars || !veil) return undefined;
    return runWater(layer, water, stars, veil, story.current, setLit);
  }, [story]);

  return (
    <Layer ref={layerRef} aria-hidden="true">
      <WaterCanvas ref={waterRef} $lit={lit} />
      <NightVeil ref={veilRef} />
      <StarsCanvas ref={starsRef} />
    </Layer>
  );
};

/** Starts the water on these elements; returns its teardown. */
function runWater(
  layer: HTMLElement,
  waterCanvas: HTMLCanvasElement,
  starsCanvas: HTMLCanvasElement,
  veil: HTMLElement,
  story: WaterStory,
  onLit: (lit: boolean) => void,
): () => void {
  const stars = createWaterStars(starsCanvas);
  let water: WaterGl | null = null;
  let cssWidth = 0;
  let cssHeight = 0;
  let metrics = stageMetrics(1, 1);

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const cadence = createFrameCadence();
  let elapsed = 0;
  let raf = 0;

  /* The cursor's wake. `pointer` is where a mouse is (null once it has
     left the page); `wake` is what the water is given, its strength
     easing up and down so the answer arrives and leaves gently. */
  let pointer: { x: number; y: number } | null = null;
  let veilNight = '0.000';
  const wake: Wake = { x: 0, y: 0, strength: 0, time: 0 };

  const paint = () => {
    // Reduced motion holds the app's still frame.
    const still = reduce.matches;
    const t = still ? STILL_T : wrap(elapsed);
    const causticTime = still ? 0 : (elapsed * CAUSTIC_SPEED) % CAUSTIC_PERIOD;
    const swirlTime = still ? 0 : elapsed % SWIRL_PERIOD;
    const frame = storyFrame(story, metrics);
    water?.draw(t, causticTime, metrics, frame, swirlTime, still ? null : wake);
    stars?.draw(t, metrics);
    // The veil works with or without WebGL: it is the page's night either way.
    const night = frame.night.toFixed(3);
    if (night !== veilNight) {
      veilNight = night;
      veil.style.opacity = night;
    }
  };
  // A still page has no loop; a section whose weight changes asks here.
  story.repaint = () => {
    if (!raf) paint();
  };

  const frame = (now: number) => {
    raf = requestAnimationFrame(frame);
    const step = cadence.tick(now);
    if (step === null) return;
    elapsed += step;

    // The water answers a pointer only while the page is resting: it
    // rises to it, and settles back when the pointer or the rest goes.
    const want = pointer && story.interactive ? 1 : 0;
    const per = step / (want > wake.strength ? WAKE.rise : WAKE.fall);
    wake.strength =
      want > wake.strength
        ? Math.min(want, wake.strength + per)
        : Math.max(want, wake.strength - per);
    if (pointer) {
      wake.x = pointer.x;
      wake.y = pointer.y;
    }
    wake.time = elapsed % WAKE_PERIOD;

    paint();
  };

  // A mouse only: a finger has no hover, and a tap would leave a wake
  // sitting wherever it last touched.
  const onPointerMove = (e: PointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    pointer = { x: e.clientX, y: e.clientY };
  };
  const onPointerGone = () => {
    pointer = null;
  };

  const start = () => {
    if (raf || reduce.matches) return;
    cadence.reset();
    raf = requestAnimationFrame(frame);
  };
  const stop = () => {
    cancelAnimationFrame(raf);
    raf = 0;
  };
  const onMotionPreference = () => {
    if (reduce.matches) {
      stop();
      paint();
    } else {
      start();
    }
  };

  // Resizing a canvas clears it, so repaint in the same frame rather
  // than show a blank one until the next tick.
  const measure = () => {
    const box = layer.getBoundingClientRect();
    cssWidth = box.width;
    cssHeight = box.height;
    metrics = stageMetrics(cssWidth, cssHeight);
    water?.resize(cssWidth, cssHeight);
    stars?.resize(cssWidth, cssHeight);
    paint();
  };
  const observer = new ResizeObserver(measure);
  observer.observe(layer);
  measure();

  // The shader compiles off the page's critical path: after the first
  // paint, when the browser is idle. Its first frame is painted here, not
  // left to the loop, because a still page has no loop.
  const lightUp = () => {
    water = createWaterGl(
      waterCanvas,
      () => {
        paint();
        onLit(true);
      },
      () => onLit(false),
    );
    water?.resize(cssWidth, cssHeight);
  };
  const canIdle = 'requestIdleCallback' in window;
  const idle = canIdle
    ? window.requestIdleCallback(lightUp, { timeout: 1500 })
    : window.setTimeout(lightUp, 200);

  reduce.addEventListener('change', onMotionPreference);
  window.addEventListener('pointermove', onPointerMove, { passive: true });
  document.addEventListener('pointerleave', onPointerGone);
  window.addEventListener('blur', onPointerGone);
  start();

  return () => {
    window.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerleave', onPointerGone);
    window.removeEventListener('blur', onPointerGone);
    if (canIdle) window.cancelIdleCallback(idle);
    else window.clearTimeout(idle);
    reduce.removeEventListener('change', onMotionPreference);
    observer.disconnect();
    stop();
    water?.destroy();
    story.repaint = null;
  };
}
